import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useNavigate } from "react-router";
import ComponentCard from "../../../../../shared/ui/common/ComponentCard";
import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import Button from "../../../../../shared/ui/button/Button";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import { GroupIcon, InfoIcon } from "../../../../../icons";
import { listAllTherapistPatients } from "../../../application/patients";
import { useCouplePatient } from "../../../application/useCouplePatient";
import { Patient } from "../../../domain/Patient";
import { useCenter, useCourseLabels } from "../../../../center";
import { PatientOwnershipChoice } from "../PatientOwnership";

interface CouplePatientFormProps {
    therapistID: string;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.4, ease: "easeOut" as const }
    },
};

export default function CouplePatientAddForm({ therapistID }: CouplePatientFormProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { createCouple, loading: creating, error: createError } = useCouplePatient();

    const [patients, setPatients] = useState<
        { value: string; label: string; heldByCenter: boolean }[]
    >([]);
    const [loadingPatients, setLoadingPatients] = useState(false);

    const [patient1Id, setPatient1Id] = useState("");
    const [patient2Id, setPatient2Id] = useState("");
    const [displayName, setDisplayName] = useState("");

    const [error, setError] = useState<string | null>(null);

    // For a therapist in a centre, a couple is the centre's or their own, and
    // both partners have to be the same: only matching patients are offered.
    // A therapist who works alone is offered everybody, as before.
    const { center } = useCenter();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    const [heldByCenter, setHeldByCenter] = useState(true);
    const offered = center
        ? patients.filter((patient) => patient.heldByCenter === heldByCenter)
        : patients;

    const chooseOwnership = (value: boolean) => {
        setHeldByCenter(value);
        // Partners picked under the other answer are no longer on offer.
        setPatient1Id("");
        setPatient2Id("");
    };

    useEffect(() => {
        if (!therapistID) return;
        setLoadingPatients(true);
        listAllTherapistPatients(therapistID)
            .then((res) => {
                const data = res.patients || [];
                const mapped = data.map((p: Patient) => ({
                    value: p.id,
                    label: labelOf(p),
                    heldByCenter: Boolean(p.center_id),
                }));
                setPatients(mapped);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoadingPatients(false));
    }, [therapistID, labelOf]);

    const handleSubmit = async () => {
        if (!patient1Id || !patient2Id) {
            setError(t("patient.selectBothPatients"));
            return;
        }
        if (patient1Id === patient2Id) {
            setError(t("patient.differentPatientsRequired"));
            return;
        }

        const result = await createCouple({
            therapist_id: therapistID,
            patient_1_id: patient1Id,
            patient_2_id: patient2Id,
            display_name: displayName || undefined,
        });

        if (result) {
            navigate(`/couple-patient/${result.id}`);
        }
    };

    return (
        <ComponentCard
            glass={true}
            title={t("patient.newCouple")}
            className="overflow-hidden shadow-xl border-white/20 dark:border-white/10"
        >
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="space-y-6"
            >
                {center && (
                    <motion.div variants={itemVariants}>
                        <PatientOwnershipChoice
                            centerName={center.name}
                            heldByCenter={heldByCenter}
                            onChange={chooseOwnership}
                            name="couple-ownership"
                            question={t("patient.ownership.coupleQuestion")}
                        />
                    </motion.div>
                )}

                <motion.div variants={itemVariants}>
                    <Label>{t("patient.patient1")}</Label>
                    <FilterSelect
                        options={offered}
                        value={patient1Id}
                        onChange={(val) => setPatient1Id(val as string)}
                        placeholder={loadingPatients ? t("common.loading") : t("patient.selectPatient")}
                        searchPlaceholder={t("patient.searchPlaceholder")}
                    />
                </motion.div>

                <motion.div variants={itemVariants}>
                    <Label>{t("patient.patient2")}</Label>
                    <FilterSelect
                        options={offered}
                        value={patient2Id}
                        onChange={(val) => setPatient2Id(val as string)}
                        placeholder={loadingPatients ? t("common.loading") : t("patient.selectPatient")}
                        searchPlaceholder={t("patient.searchPlaceholder")}
                    />
                </motion.div>

                <motion.div variants={itemVariants}>
                    <Label>{t("patient.coupleDisplayName")} ({t("common.optional")})</Label>
                    <Input
                        type="text"
                        startIcon={<GroupIcon className="size-5" />}
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder={t("patient.coupleDisplayNamePlaceholder")}
                    />
                </motion.div>

                <motion.div
                    variants={itemVariants}
                    className="bg-brand-50/50 dark:bg-brand-900/10 border border-brand-100 dark:border-brand-800/50 rounded-xl p-4 flex gap-3"
                >
                    <div className="mt-0.5">
                        <InfoIcon className="size-5 text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-brand-900 dark:text-brand-100 mb-1">
                            {t("patient.coupleNoticeTitle")}
                        </h4>
                        <p className="text-xs text-brand-700/80 dark:text-brand-400/80 leading-relaxed">
                            {t("patient.coupleNoticeDesc")}
                        </p>
                    </div>
                </motion.div>

                <motion.div variants={itemVariants} className="flex items-center justify-end pt-4 gap-3">
                    <Button
                        variant="outline"
                        className="px-8"
                        size="md"
                        onClick={() => navigate("/patients")}
                    >
                        {t("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={creating}
                        disabled={creating}
                        className="px-8 min-w-[120px]"
                        size="md"
                    >
                        {t("common.save")}
                    </Button>
                </motion.div>

                {(error || createError) && (
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 dark:text-red-400 text-sm text-center font-medium"
                    >
                        {error || createError}
                    </motion.p>
                )}
            </motion.div>
        </ComponentCard>
    );
}
